import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { LayoutDashboard, Users, FolderKanban, UserCircle, BarChart3, Search, Menu, X, LogOut, ChevronRight, Settings, Database, Archive, Layers, Pin, PinOff } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import NotificationsBell from '@/components/NotificationsBell';
import CommandPalette from '@/components/CommandPalette';

const navItems = [
  { path: '/', label: 'Studio', icon: LayoutDashboard },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/projects', label: 'Projects', icon: FolderKanban },
  { path: '/drawings', label: 'Drawings', icon: Layers },
  { path: '/team', label: 'Team', icon: UserCircle },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/archive', label: 'Archive', icon: Archive },
  { path: '/databases', label: 'Databases', icon: Database },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const PIN_KEY = 'is_sidebar_pinned';

export default function Layout() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [pinned, setPinned] = useState(() => localStorage.getItem(PIN_KEY) !== 'false');
  const [hovering, setHovering] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchSeed, setSearchSeed] = useState('');
  const searchInputRef = useRef(null);

  const expanded = pinned || hovering; // desktop: show labels when pinned or hovered

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchSeed('');
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (searchExpanded) requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [searchExpanded]);

  const openSearch = (seed = '') => { setSearchSeed(seed); setSearchOpen(true); };
  const togglePin = () => setPinned(p => { localStorage.setItem(PIN_KEY, String(!p)); return !p; });

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  const studioName = branding.accountName || 'Interior Story';
  const firstName = (user?.name || '').trim().split(' ')[0] || 'My';

  const LogoMark = ({ size = 36 }) => (
    branding.logoUrl
      ? <div className="rounded-lg bg-white shrink-0 flex items-center justify-center overflow-hidden" style={{ width: size, height: size, padding: 3 }}>
          <img src={branding.logoUrl} alt={studioName} className="max-w-full max-h-full object-contain" />
        </div>
      : <div className="rounded-lg bg-white/15 flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
          <span className="text-white font-bold font-['Manrope']" style={{ fontSize: size * 0.42 }}>{getInitials(studioName)}</span>
        </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--app-bg-rgb))' }}>
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar — teal, collapsible. Collapsed = icon strip; expands on hover unless pinned. */}
      <aside
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={`fixed top-0 left-0 h-screen bg-[#3A8C8C] text-white z-50 shadow-xl transition-all duration-300 ease-out
          ${sidebarOpen ? 'translate-x-0 w-60' : '-translate-x-full'} md:translate-x-0 ${expanded ? 'md:w-60' : 'md:w-16'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header: logo + studio name (display only) + pin toggle top-right */}
          <div className="h-[68px] px-3 flex items-center gap-2.5 border-b border-white/10">
            <LogoMark size={36} />
            {expanded && (
              <>
                <span className="flex-1 min-w-0 font-semibold text-white text-base font-['Manrope'] tracking-tight truncate" data-testid="studio-name">{studioName}</span>
                <button
                  onClick={togglePin}
                  title={pinned ? 'Unpin — auto-hide on hover' : 'Pin — keep sidebar open'}
                  className="hidden md:flex shrink-0 w-7 h-7 items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors"
                  data-testid="sidebar-pin-toggle"
                >
                  {pinned ? <Pin size={15} /> : <PinOff size={15} />}
                </button>
                <button className="md:hidden shrink-0 text-white/80" onClick={() => setSidebarOpen(false)}>
                  <X size={20} strokeWidth={1.5} />
                </button>
              </>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
            {navItems.map(({ path, label, icon: Icon }) => {
              const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
              return (
                <NavLink
                  key={path}
                  to={path}
                  title={label}
                  data-testid={`nav-${label.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 h-10 rounded-xl text-sm font-medium transition-colors ${expanded ? 'px-3' : 'px-0 justify-center'} ${
                    isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon size={20} strokeWidth={1.5} className="shrink-0" />
                  {expanded && <span className="truncate">{label}</span>}
                </NavLink>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-2 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2.5 w-full h-11 rounded-xl hover:bg-white/10 transition-colors ${expanded ? 'px-2' : 'px-0 justify-center'}`} data-testid="user-menu-trigger">
                  {/* Initials only — no profile photo in the panel */}
                  <Avatar className="w-8 h-8 shrink-0">
                    <AvatarFallback className="bg-white/20 text-white text-xs">{getInitials(user?.name)}</AvatarFallback>
                  </Avatar>
                  {expanded && (
                    <>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                        <p className="text-xs text-white/60 capitalize truncate">{user?.role?.replace('_', ' ')}</p>
                      </div>
                      <ChevronRight size={14} strokeWidth={1.5} className="text-white/60 shrink-0" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-xs text-[#6B7280]">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-[#C4727F]">
                  <LogOut size={14} strokeWidth={1.5} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content — left padding follows the pinned width */}
      <div className={`transition-all duration-300 ${pinned ? 'md:pl-60' : 'md:pl-16'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 backdrop-blur-md border-b border-[#E8E6E1]" style={{ backgroundColor: 'rgb(var(--app-bg-rgb) / 0.8)' }}>
          <div className="flex items-center justify-between px-4 md:px-8 py-3">
            {/* Left: mobile menu + personalized dashboard title. On hover-expand (unpinned)
                the title shifts right so the teal panel never covers it. */}
            <div
              className="flex items-center gap-3 min-w-0 transition-[padding] duration-300"
              style={{ paddingLeft: (!pinned && hovering) ? '11rem' : undefined }}
            >
              <button className="md:hidden p-1.5 rounded-lg hover:bg-[#E8E6E1]/50" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <Menu size={20} strokeWidth={1.5} />
              </button>
              <h1 className="text-base md:text-lg font-['Manrope'] font-semibold text-[#2C2C2E] truncate" data-testid="dashboard-title">
                {firstName}&apos;s Dashboard
              </h1>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Expanding global search */}
              <div
                className={`flex items-center h-9 rounded-xl overflow-hidden transition-all duration-300 ease-out ${searchExpanded ? 'w-40 sm:w-64 bg-white border border-[#E8E6E1] focus-within:border-[#3A8C8C]/40' : 'w-9 border border-transparent'}`}
                data-testid="global-search"
              >
                <button
                  type="button"
                  onClick={() => (searchExpanded ? openSearch('') : setSearchExpanded(true))}
                  className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#E8E6E1]/50 transition-colors"
                  aria-label="Search"
                  data-testid="search-toggle"
                >
                  <Search size={searchExpanded ? 16 : 20} strokeWidth={1.5} />
                </button>
                {searchExpanded && (
                  <input
                    ref={searchInputRef}
                    value=""
                    placeholder="Search"
                    onChange={(e) => { if (e.target.value) openSearch(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setSearchExpanded(false); }}
                    onBlur={() => setTimeout(() => setSearchExpanded(false), 120)}
                    className="flex-1 min-w-0 h-9 bg-transparent text-sm text-[#2C2C2E] placeholder:text-[#9CA3AF] outline-none pr-3"
                    data-testid="global-search-input"
                    aria-label="Search"
                  />
                )}
              </div>
              <NotificationsBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-8 lg:p-10 max-w-[1600px]">
          <Outlet />
        </main>
      </div>

      {/* Global command palette */}
      <CommandPalette
        open={searchOpen}
        initialQuery={searchSeed}
        onOpenChange={(o) => { setSearchOpen(o); if (!o) { setSearchSeed(''); setSearchExpanded(false); } }}
      />
    </div>
  );
}
